import { useEffect, useState } from 'react';
import { useIsFetching } from '@tanstack/react-query';

export type SyncStatus = 'synced' | 'syncing' | 'offline';

/**
 * Derives the real top-bar sync state from the browser's connectivity and
 * React Query's global activity, instead of a hardcoded "Synced" pill.
 *
 * - `offline`  — `navigator.onLine` is false (overrides everything; a field
 *   tech with no signal must never see a green "Synced").
 * - `syncing`  — online and at least one query is in flight.
 * - `synced`   — online and idle.
 *
 * Why it matters: this is an offline-aware field app. A permanently-green pill
 * lies to the user about whether their data is actually reaching the server.
 */
export function useSyncStatus(): SyncStatus {
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  const fetchingCount = useIsFetching();

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    // Resync from the source of truth in case it changed before listeners attached.
    setIsOnline(navigator.onLine);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!isOnline) return 'offline';
  if (fetchingCount > 0) return 'syncing';
  return 'synced';
}
